"use client";

import {
  VideoJS,
  VideoPlayerSurface,
  getViewingTokenForSpecial,
} from "@punchup/video/client";
import {
  addToWatchedSpecials,
  getWatchedSpecials,
} from "@/components/autoplay/autoplayQueue";
import { VideoEvents, trackEvent } from "@punchup/analytics";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Player from "video.js/dist/types/player";
import { SpecialRow } from "@/data/specials";
import { createPortal } from "react-dom";
import { getCDNImageURL } from "@/data/images";
import { useAutoplayNext } from "@/hooks/autoplay/useAutoplayNext";
import { usePersistentPlayer } from "./PersistentPlayerProvider";
import { useRouter } from "next/navigation";

// How many "what plays next" picks to prefetch each play.
const AUTOPLAY_RECS_LIMIT = 2;

/**
 * Whether the currently-loaded special (`currentId`) is stale relative to what
 * the page wants playing (`mediaId`), and so should be hidden until the new
 * source resolves.
 *
 * It's stale when the page wants a DIFFERENT special than what's loaded AND
 * we're not mid self-advance. During a self-advance the host sets `current` to
 * the next special before navigation lands, so `current` legitimately leads
 * `media` — hiding then would blank the player through the transition. For an
 * external navigation (e.g. feed → a different special) `selfAdvancing` is
 * false, so the previous video is correctly hidden instead of flashing/playing
 * for the moment before the new token resolves.
 */
export function isPreviousSpecialStale(
  currentId: string | null,
  mediaId: string | null,
  selfAdvancing: boolean
): boolean {
  return !!currentId && !!mediaId && currentId !== mediaId && !selfAdvancing;
}

/** A placeholder's position in DOCUMENT coordinates (page-relative, not viewport). */
type SlotRect = { top: number; left: number; width: number };

/**
 * Tracks a placeholder element's position so the host can overlay it.
 *
 * The position is measured in DOCUMENT coordinates (adding scroll offset) so the
 * player can be `position: absolute` and scroll natively WITH the page. The
 * earlier approach measured the viewport rect and applied it with
 * `position: fixed`, which had to be re-applied via this scroll handler every
 * frame — so the player visibly trailed the slot by a frame on scroll. In
 * document space the value is invariant under scroll, so the browser moves the
 * player and the page in the same paint (no lag), and we only re-render when the
 * slot's page position actually changes (resize / layout shift).
 */
function useSlotRect(slotEl: HTMLElement | null): SlotRect | null {
  const [rect, setRect] = useState<SlotRect | null>(null);

  useEffect(() => {
    if (!slotEl) {
      setRect(null);
      return;
    }
    const update = () =>
      setRect((prev) => {
        const r = slotEl.getBoundingClientRect();
        const next: SlotRect = {
          top: r.top + window.scrollY,
          left: r.left + window.scrollX,
          width: r.width,
        };
        // A pure scroll yields an identical document rect every frame; bail so
        // we don't re-render the player on each scroll tick (the player is
        // absolutely positioned, so it already scrolled with the page).
        if (
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width
        ) {
          return prev;
        }
        return next;
      });
    update();

    const ro = new ResizeObserver(update);
    ro.observe(slotEl);
    // Capture-phase scroll catches scrolling in any ancestor container (so the
    // document position is recomputed if scrolling shifts layout, e.g. a sticky
    // header collapsing or mobile browser chrome resizing the viewport).
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [slotEl]);

  return rect;
}

/**
 * The navigation-surviving video player.
 *
 * Mounted once in the root layout, it renders a single `<VideoJS>` instance and
 * positions it (via a portal + `position: fixed`) over whatever `<HeroSlot/>`
 * the current special page registered. Because the player element never
 * unmounts, swapping its source keeps the user's play-authorization — so
 * autoplay continues across cross-comedian navigations instead of being blocked.
 *
 * This is where the autoplay controller now lives (moved out of the page-side
 * wrapper, which dies on navigation): current special, source resolution, the
 * `useAutoplayNext` loop, and the end-screen wiring.
 */
export function PersistentPlayerHost() {
  const { media, slotEl, setPlayerHeight } = usePersistentPlayer();
  const router = useRouter();
  const rect = useSlotRect(slotEl);
  const playerRef = useRef<Player | null>(null);
  const hostElRef = useRef<HTMLDivElement | null>(null);

  const [current, setCurrent] = useState<SpecialRow | null>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState<boolean>(true);
  const [poster, setPoster] = useState<string>("");
  const [showEndOverlay, setShowEndOverlay] = useState<boolean>(false);
  const [nextUp, setNextUp] = useState<SpecialRow | null>(null);
  const [nextUpAlternate, setNextUpAlternate] = useState<SpecialRow | null>(
    null
  );

  // Mirror of `current` for reads inside effects that must NOT re-run when the
  // host advances itself (see the adopt-effect below).
  const currentRef = useRef<SpecialRow | null>(null);
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  // True between us initiating a self-advance (`playNext`) and the page's
  // `media` catching up to it. While true, `current` is intentionally AHEAD of
  // `media` (we already loaded the next special; navigation hasn't landed yet),
  // so the staleness guard in the render path must NOT hide the player. For an
  // external navigation (e.g. feed → a different special) this stays false, so
  // the guard correctly hides the previous special until the new one resolves.
  const selfAdvancingRef = useRef(false);

  // --- Adopt whatever the current page wants playing ---------------------
  // Runs ONLY when `media` changes (a page registered a special). It must not
  // depend on `current`: after we advance ourselves via `playNext`, `current`
  // is the NEW special while the still-mounted page's `media` is the OLD one —
  // re-running here would reload the old video (a flash) before the new page
  // updates `media`. Skip when we're already playing it (a continuation).
  useEffect(() => {
    if (!media) return;
    if (currentRef.current?.id === media.special.id) {
      // The page navigated to the special we already advanced ourselves to
      // (self-advance landing): the element is already playing it, so don't
      // reload. Media has now caught up, so clear the self-advance flag.
      selfAdvancingRef.current = false;
      return;
    }

    // A genuinely different special — the user navigated here (e.g. from the
    // feed) rather than us auto-advancing. Tear down the previous source NOW,
    // synchronously, so the old video can't keep rendering/playing during the
    // async token fetch below (the "previous video plays for a second" bug).
    // The render guard also hides the player while `current` is stale.
    selfAdvancingRef.current = false;
    setVideoSrc(null);
    setShowEndOverlay(false);
    setNextUp(null);
    setNextUpAlternate(null);

    let cancelled = false;
    (async () => {
      const special = media.special;
      const poster = special.preview_img
        ? getCDNImageURL(special.preview_img)
        : media.posterImg;
      let src: string | null = null;
      let preview = true;
      if (special.video_id && media.userCanAccessSpecial) {
        const tokenRes = await getViewingTokenForSpecial(special.id);
        src = tokenRes?.hasAccess ? tokenRes.token : null;
        preview = false;
      }
      if (cancelled) return;
      setPoster(poster);
      setVideoSrc(src);
      setIsPreview(preview);
      setCurrent(special);
    })();
    return () => {
      cancelled = true;
    };
  }, [media]);

  // --- Autoplay loop (prefetched per play) -------------------------------
  const sessionPlayedIds = useMemo(
    () => getWatchedSpecials(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current?.id]
  );
  // The host is "active" for the persistent-player cohort whenever a special
  // page has registered media — which only happens when the server gate passed
  // (see PersistentPlayerProvider). That is the single enablement signal; there
  // is no separate client gate to fall out of sync with.
  const isActive = Boolean(media);
  const { data: nextSpecials } = useAutoplayNext({
    specialId: current?.id,
    sessionPlayedIds,
    limit: AUTOPLAY_RECS_LIMIT,
    enabled: isActive,
  });

  // Instrumentation: log the queue the endpoint returned for the special now
  // playing, so we can see (a) how many picks came back — 0 means autoplay
  // dead-stops — and (b) the top pick's shape, to catch a "bad" special that
  // would halt auto-advance. Auto-advance can only fire if the top pick has
  // BOTH a playable `video_id` and a `comedian_slug` to navigate to (see
  // `playNext` / `handleVideoEnded`); `would_halt` flags responses that don't.
  // `currentRef` is read (not a dep) so we log the special the queue was
  // computed for and fire once per resolved response, not on every `current`
  // change. `undefined` is the react-query loading state; `[]` is a real
  // (halting) result we DO want to log.
  useEffect(() => {
    if (!isActive || nextSpecials === undefined) return;
    const fromSpecial = currentRef.current;
    const nextUp = nextSpecials[0] ?? null;
    trackEvent(VideoEvents.AUTOPLAY_QUEUE_LOADED, {
      from_special_id: fromSpecial?.id ?? null,
      from_comedian_id: fromSpecial?.comedian_id ?? null,
      result_count: nextSpecials.length,
      next_special_id: nextUp?.id ?? null,
      next_special_slug: nextUp?.slug ?? null,
      next_comedian_slug: nextUp?.comedian_slug ?? null,
      next_has_video_id: Boolean(nextUp?.video_id),
      next_requires_subscription: nextUp?.requires_subscription ?? null,
      next_score: nextUp?.score ?? null,
      would_halt:
        nextSpecials.length === 0 ||
        !nextUp?.video_id ||
        !nextUp?.comedian_slug,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextSpecials, isActive]);

  // Warm the next posters while this special plays.
  useEffect(() => {
    if (typeof window === "undefined" || !nextSpecials) return;
    nextSpecials.forEach((s) => {
      const p = s.poster_img_with_titles ?? s.preview_img;
      if (p) new window.Image().src = getCDNImageURL(p);
    });
  }, [nextSpecials]);

  // --- Play a specific next special, persistently ------------------------
  // Resolve the token and update the source/special; the player keeps its
  // element (so playback authorization survives), then we navigate so the rest
  // of the page becomes the real server-rendered page for that special. On the
  // new page, `media.special.id === current.id`, so the adopt-effect no-ops.
  const playNext = useCallback(
    async (next: SpecialRow) => {
      if (current) addToWatchedSpecials(current.id);

      const url = nextSpecials?.find((s) => s.id === next.id)?.comedian_slug
        ? `/${nextSpecials.find((s) => s.id === next.id)!.comedian_slug}/${
            next.slug
          }?autoplay=1`
        : null;

      let token = null;
      if (next.video_id) token = await getViewingTokenForSpecial(next.id);

      if (token?.hasAccess) {
        // We're advancing ourselves: `current` is about to lead `media` until
        // the navigation below lands. Flag it so the staleness guard keeps the
        // player visible through the transition instead of blanking it.
        selfAdvancingRef.current = true;
        setPoster(next.preview_img ? getCDNImageURL(next.preview_img) : poster);
        setVideoSrc(token.token);
        setIsPreview(false);
        setShowEndOverlay(false);
        setNextUp(null);
        setNextUpAlternate(null);
        setCurrent(next);
      }

      // Navigate for the page chrome. If we couldn't get access, this lands on
      // the special page whose server logic redirects to checkout/purchase.
      if (url) router.push(url);
    },
    [current, nextSpecials, poster, router]
  );

  // --- End-of-video → show the end screen --------------------------------
  const handleVideoEnded = useCallback(() => {
    if (current) addToWatchedSpecials(current.id);
    const next = nextSpecials?.[0] ?? null;
    if (!isActive || !next) {
      setShowEndOverlay(false);
      return;
    }
    setNextUp(next);
    setNextUpAlternate(nextSpecials?.[1] ?? null);
    setShowEndOverlay(true);
  }, [isActive, current, nextSpecials]);

  // Keep the reported height in sync as the player loads / the control bar
  // appears. Re-attaches when the player (re)mounts or the slot resizes.
  useEffect(() => {
    const el = hostElRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setPlayerHeight(el.getBoundingClientRect().height)
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, [setPlayerHeight, videoSrc, rect]);

  // Hide the previous special until the adopt effect resolves the new source,
  // instead of flashing/playing the old one when the user navigates here.
  const isStaleCurrent = isPreviousSpecialStale(
    current?.id ?? null,
    media?.special.id ?? null,
    selfAdvancingRef.current
  );

  if (!media || !current || !videoSrc || !rect || isStaleCurrent) return null;

  return createPortal(
    <div
      ref={(el) => {
        hostElRef.current = el;
        if (el) setPlayerHeight(el.getBoundingClientRect().height);
      }}
      style={{
        // Absolute (document-anchored) so the player scrolls WITH the page in a
        // single paint — `fixed` had to be repositioned via JS on every scroll
        // frame and visibly lagged the slot. `rect` is already in document
        // coordinates (see useSlotRect).
        position: "absolute",
        top: rect.top,
        left: rect.left,
        width: rect.width,
        // Height is intentionally NOT set: the player's vjs-16-9 derives its own
        // height from this width. Setting height here too would double-count it
        // (the player overflows / looks too tall).
        zIndex: 30,
      }}
    >
      <VideoJS
        src={videoSrc}
        poster={poster}
        lastPlaybackLocation={media.lastPlaybackLocation}
        special={current}
        isPreview={isPreview}
        surface={VideoPlayerSurface.ComedianSpecial}
        context={media.context}
        nextUpSpecial={nextUp}
        secondaryNextSpecial={nextUpAlternate}
        showEndOverlay={showEndOverlay}
        playerRef={playerRef}
        onPlayNext={playNext}
        onCancel={() => {
          setShowEndOverlay(false);
          setNextUp(null);
          setNextUpAlternate(null);
        }}
        onPlayStatusChanged={(s) => s === "ENDED" && handleVideoEnded()}
      />
    </div>,
    document.body
  );
}
